import { makeAutoObservable } from 'mobx';
import moment, { Moment } from 'moment';
import { StatusMessageEnum, StatisticsIntervalTypeEnum } from '@app/common';
import type { RootStore } from '../root-store';
import { OverviewStore, OverviewStoreHydration } from './overview-store';
import { ProfitStore } from './profit-store';
import { RatiosStore, RatiosStoreHydration } from './ratios-store';
import { StatisticsIntervalInterface } from '../../interfaces/statistics-interval-interface';
// eslint-disable-next-line import/no-cycle
import { InvestownAssetMonthlyStatusStore } from '../investown-asset-monthly-status-store';

export type StatisticStoreHydration = OverviewStoreHydration &
    RatiosStoreHydration & {
        type: StatisticsIntervalTypeEnum;
        ratiosType: StatisticsIntervalTypeEnum;
    };

export const STATISTIC_INTERVALS: StatisticsIntervalInterface[] = [
    {
        title: 'Za celou dobu',
        dateFrom: null,
        type: StatisticsIntervalTypeEnum.AllTime,
    },
    {
        title: 'Posledních 12 měsíců',
        dateFrom: moment().subtract(1, 'year').subtract(1, 'month').endOf('month').startOf('day'),
        type: StatisticsIntervalTypeEnum.LastYearBeginning,
    },
    {
        title: 'Od začátku roku',
        dateFrom: moment().startOf('year').subtract(1, 'day').startOf('day'),
        type: StatisticsIntervalTypeEnum.ThisYearBeginning,
    },
    {
        title: 'Posledních 30 dní',
        // 29 protože je to 30 dní včetně dneška
        dateFrom: moment().subtract(29, 'day').startOf('day'),
        type: StatisticsIntervalTypeEnum.LastThirtyDays,
    },
    {
        title: 'Posledních 7 dní',
        // 6 protože je to 7 dní včetně dneška
        dateFrom: moment().subtract(6, 'day').startOf('day'),
        type: StatisticsIntervalTypeEnum.LastSevenDays,
    },
    {
        title: 'Dnes',
        dateFrom: moment().subtract(1, 'day').startOf('day'),
        dateTo: moment().startOf('day'),
        type: StatisticsIntervalTypeEnum.Today,
    },
];

export class StatisticStore {
    rootStore: RootStore;

    overviewStore: OverviewStore;

    profitStore: ProfitStore;

    ratiosStore: RatiosStore;

    investownAssetMonthlyStatusStore: InvestownAssetMonthlyStatusStore;

    type: StatisticsIntervalTypeEnum = StatisticsIntervalTypeEnum.AllTime;

    ratiosType: StatisticsIntervalTypeEnum = StatisticsIntervalTypeEnum.AllTime;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.overviewStore = new OverviewStore(this);
        this.profitStore = new ProfitStore(this);
        this.ratiosStore = new RatiosStore(this);
        this.investownAssetMonthlyStatusStore = new InvestownAssetMonthlyStatusStore(this);

        makeAutoObservable(this);
    }

    startLoading(): void {
        this.overviewStore.startLoading();
        this.profitStore.startLoading();
        this.ratiosStore.startLoading();
        this.investownAssetMonthlyStatusStore.startLoading();
    }

    stopLoading(): void {
        this.overviewStore.stopLoading();
        this.profitStore.stopLoading();
        this.ratiosStore.stopLoading();
        this.investownAssetMonthlyStatusStore.stopLoading();
    }

    get loading() {
        // TODO: speed
        return this.overviewStore.loading || this.ratiosStore.loading || this.profitStore.loading;
        // return this.overviewStore.loading || this.ratiosStore.loading;
    }

    getInterval(type: StatisticsIntervalTypeEnum): StatisticsIntervalInterface {
        const interval = STATISTIC_INTERVALS.find((si) => si.type === type);
        if (!interval) {
            throw new Error(StatusMessageEnum.SelectedIntervalNotFound);
        }
        return interval;
    }

    get dateFrom(): Moment | null {
        const interval = this.getInterval(this.type);
        return interval.dateFrom;
    }

    get intervalType(): StatisticsIntervalTypeEnum {
        const interval = this.getInterval(this.type);
        return interval.type;
    }

    get dateTo(): Moment | null | undefined {
        const interval = this.getInterval(this.type);
        return interval.dateTo;
    }

    get ratiosDateFrom(): Moment | null {
        const interval = this.getInterval(this.ratiosType);
        return interval.dateFrom;
    }

    get ratiosDateTo(): Moment | null | undefined {
        const interval = this.getInterval(this.ratiosType);
        return interval.dateTo;
    }

    get chartDateFormat(): string {
        if (
            this.type === StatisticsIntervalTypeEnum.LastSevenDays ||
            this.type === StatisticsIntervalTypeEnum.LastThirtyDays
        ) {
            return process.env.NEXT_PUBLIC_DATE_FORMAT as string;
        }
        return process.env.NEXT_PUBLIC_DATE_YEAR_MONTH_FORMAT as string;
    }

    async fetchIndexData(): Promise<void> {
        await Promise.all([this.overviewStore.fetchOverview(), this.ratiosStore.fetchRatios()]);
    }

    init(): void {
        if (this.rootStore.authStore.userHasFreeTariff) {
            this.type = StatisticsIntervalTypeEnum.LastThirtyDays;
            this.ratiosType = StatisticsIntervalTypeEnum.LastThirtyDays;
        }
    }

    async setType(type: StatisticsIntervalTypeEnum): Promise<void> {
        this.type = type;
        await this.profitStore.fetchProfitCharts(false);
    }

    async setRatiosType(type: StatisticsIntervalTypeEnum): Promise<void> {
        this.ratiosType = type;
        this.ratiosStore.invalidateFullScreenRatios();
        await this.ratiosStore.fetchRatios();
    }
}
